import { selectAuthError, selectAuthLoading } from "@/entities/auth"
import { useAppDispatch, useAppSelector } from "@/shared/lib/redux"
import { FormInput } from "@/shared/ui/form-input"
import { Controller, SubmitHandler, useForm } from "react-hook-form"
import { PhoneInput } from "./phone-input"
import { useNavigate } from "react-router-dom"
import { yupResolver } from "@hookform/resolvers/yup"
import * as yup from 'yup'
import WarningSvg from '@/shared/assets/images/Warning.svg'
import { getErrorMessage } from "@/shared/consts/error-codes"
import AuthPreview from '@/shared/assets/images/AuthPreview.png'
import LogoMonoSvg from '@/shared/assets/images/LogoMono.svg'
import { IRegisterOrganizationRequest, requestPhoneConfirmationCode, requestEmailConfirmationCode } from "@/shared/api/onboarding"
import { fetchRegisterOrganization } from "@/entities/auth/model"
import { useState, useEffect, useRef } from "react"
import { E164_REGEX } from "@/shared/consts"
import { getPartyByInn } from "@/shared/api/dadata"

type RegistrationForm = {
    inn: string
    kpp?: string
    organization_name: string
    legal_full_name?: string
    registration_address?: string
    email: string
    phone: string
    password: string
    confirm_password: string
    phone_confirmation_code: string
    email_confirmation_code: string
}

const initial: RegistrationForm = {
    inn: "",
    organization_name: "",
    email: "",
    phone: "+7",
    password: "",
    confirm_password: "",
    phone_confirmation_code: "",
    email_confirmation_code: ""
}

const schema = yup.object().shape({
    inn: yup
        .string()
        .required("Введите ИНН")
        .test("inn-format", "ИНН должен содержать 10 или 12 цифр и состоять только из цифр.", (value) => {
            if (!value) return false
            if (!/^\d+$/.test(value)) return false
            return value.length === 10 || value.length === 12
        }),
    kpp: yup
        .string()
        .test("kpp-validation", "КПП обязательно для организаций и должно содержать 9 цифр", function (value) {
            const inn = this.parent.inn
            // Если ИНН содержит 12 цифр (ИП), то КПП должно быть пустым
            if (inn && /^\d{12}$/.test(inn)) {
                return !value || value.trim() === ""
            }
            // Если ИНН содержит 10 цифр (организация), то КПП обязательно и должно содержать 9 цифр
            if (inn && /^\d{10}$/.test(inn)) {
                if (!value || value.trim() === "") {
                    return this.createError({ message: "КПП обязательно для организаций" })
                }
                if (!/^\d{9}$/.test(value)) {
                    return this.createError({ message: "КПП должно содержать 9 цифр" })
                }
            }
            return true
        }),
    organization_name: yup.string().required("Введите наименование организации").min(5, "Наименование организации должно содержать минимум 5 символов"),
    legal_full_name: yup.string().required("Введите полное наименование организации").min(5, "Полное наименование организации должно содержать минимум 5 символов"),
    registration_address: yup.string().required("Введите адрес регистрации").min(5, "Адрес регистрации должен содержать минимум 5 символов"),
    email: yup.string().required("Введите email").email("Введите корректный email"),
    phone: yup.string().matches(E164_REGEX, 'Некорректный номер телефона'),
    password: yup.string().required("Введите пароль").min(8, "Пароль должен содержать минимум 8 символов"),
    confirm_password: yup.string().oneOf([yup.ref('password')], 'Пароли не совпадают'),
    phone_confirmation_code: yup.string().required("Введите код подтверждения"),
    email_confirmation_code: yup.string().required("Введите код подтверждения email")
})

export const CompanyRegistrationPage = () => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const authError = useAppSelector(selectAuthError)
    const isLoading = useAppSelector(selectAuthLoading)

    const [phoneConfirmationId, setPhoneConfirmationId] = useState<string | null>(null)
    const [isSendingCode, setIsSendingCode] = useState(false)
    const [codeSent, setCodeSent] = useState(false)
    const [resendTimer, setResendTimer] = useState(0)
    const [sendCodeError, setSendCodeError] = useState<string | null>(null)
    const previousPhoneRef = useRef<string>("")

    const [emailConfirmationId, setEmailConfirmationId] = useState<string | null>(null)
    const [isSendingEmailCode, setIsSendingEmailCode] = useState(false)
    const [emailCodeSent, setEmailCodeSent] = useState(false)
    const [emailResendTimer, setEmailResendTimer] = useState(0)
    const [sendEmailCodeError, setSendEmailCodeError] = useState<string | null>(null)
    const previousEmailRef = useRef<string>("")

    const [isLoadingDadata, setIsLoadingDadata] = useState(false)
    const [dadataError, setDadataError] = useState<string | null>(null)
    const [isAutoFilled, setIsAutoFilled] = useState(false)
    const dadataTimeoutRef = useRef<NodeJS.Timeout | null>(null)
    const previousInnRef = useRef<string>("")

    const {
        register,
        control,
        handleSubmit,
        watch,
        trigger,
        setError,
        setValue,
        formState: { errors },
    } = useForm({
        defaultValues: initial,
        resolver: yupResolver(schema)
    })

    const phoneValue = watch("phone")
    const emailValue = watch("email")
    const innValue = watch("inn")

    useEffect(() => {
        if (innValue) {
            trigger("kpp")
        }
    }, [innValue, trigger])

    // DaData integration - fetch company data by INN
    useEffect(() => {
        if (dadataTimeoutRef.current) {
            clearTimeout(dadataTimeoutRef.current)
        }

        if (innValue !== previousInnRef.current) {
            setIsAutoFilled(false)
            setDadataError(null)
        }
        previousInnRef.current = innValue || ""

        const isValidInn = innValue && /^\d{10}$|^\d{12}$/.test(innValue)

        if (!isValidInn || !innValue) {
            return
        }

        dadataTimeoutRef.current = setTimeout(async () => {
            setIsLoadingDadata(true)
            setDadataError(null)

            try {
                const response = await getPartyByInn({ inn: innValue, count: 1 })

                if (response.data && response.data.length > 0) {
                    const companyData = response.data[0]

                    if (companyData.name) {
                        const organizationName = companyData.name.short_with_opf ||
                            companyData.name.short ||
                            companyData.name.full_with_opf ||
                            companyData.name.full || ""
                        if (organizationName) {
                            setValue("organization_name", organizationName, { shouldValidate: true })
                        }

                        const legalFullName = companyData.name.full_with_opf ||
                            companyData.name.full ||
                            companyData.name.short_with_opf ||
                            companyData.name.short || ""
                        if (legalFullName) {
                            setValue("legal_full_name", legalFullName, { shouldValidate: true })
                        }
                    }

                    if (companyData.kpp) {
                        setValue("kpp", companyData.kpp, { shouldValidate: true })
                    }

                    if (companyData.address) {
                        const address = companyData.address.value ||
                            companyData.address.unrestricted_value || ""
                        if (address) {
                            setValue("registration_address", address, { shouldValidate: true })
                        }
                    }

                    setIsAutoFilled(true)
                    setDadataError(null)
                } else {
                    setIsAutoFilled(false)
                    setDadataError(null)
                }
            } catch (error: any) {
                setIsAutoFilled(false)
                setDadataError("Не удалось получить данные организации")
            } finally {
                setIsLoadingDadata(false)
            }
        }, 500)

        return () => {
            if (dadataTimeoutRef.current) {
                clearTimeout(dadataTimeoutRef.current)
            }
        }
    }, [innValue, setValue])

    useEffect(() => {
        if (phoneValue !== previousPhoneRef.current && previousPhoneRef.current !== "") {
            setPhoneConfirmationId(null)
            setCodeSent(false)
            setResendTimer(0)
            setSendCodeError(null)
        }
        previousPhoneRef.current = phoneValue || ""
    }, [phoneValue])

    useEffect(() => {
        if (emailValue !== previousEmailRef.current && previousEmailRef.current !== "") {
            setEmailConfirmationId(null)
            setEmailCodeSent(false)
            setEmailResendTimer(0)
            setSendEmailCodeError(null)
        }
        previousEmailRef.current = emailValue || ""
    }, [emailValue])

    useEffect(() => {
        if (resendTimer > 0) {
            const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000)
            return () => clearTimeout(timer)
        }
    }, [resendTimer])

    useEffect(() => {
        if (emailResendTimer > 0) {
            const timer = setTimeout(() => setEmailResendTimer(emailResendTimer - 1), 1000)
            return () => clearTimeout(timer)
        }
    }, [emailResendTimer])

    const handleSendConfirmationCode = async () => {
        if (!phoneValue || phoneValue.trim() === "") {
            setSendCodeError("Введите номер телефона")
            return
        }

        setIsSendingCode(true)
        setSendCodeError(null)

        try {
            const response = await requestPhoneConfirmationCode({ phone: phoneValue })
            setPhoneConfirmationId(response.data.id)
            setCodeSent(true)
            setResendTimer(60)
            setSendCodeError(null)
        } catch (error: any) {
            const errorId = error?.response?.data?.error_id
            const errorMessage = getErrorMessage(errorId) || "Ошибка при отправке кода"
            setSendCodeError(errorMessage)

            if (errorId === 'phone_confirmation_invalid' ||
                errorId === 'phone_not_confirmed') {
                setPhoneConfirmationId(null)
                setCodeSent(false)
            }
        } finally {
            setIsSendingCode(false)
        }
    }

    const handleSendEmailConfirmationCode = async () => {
        if (!emailValue || emailValue.trim() === "") {
            setSendEmailCodeError("Введите email")
            return
        }

        setIsSendingEmailCode(true)
        setSendEmailCodeError(null)

        try {
            const response = await requestEmailConfirmationCode({ email: emailValue })
            setEmailConfirmationId(response.data.id)
            setEmailCodeSent(true)
            setEmailResendTimer(60)
            setSendEmailCodeError(null)
        } catch (error: any) {
            const errorId = error?.response?.data?.error_id
            const errorMessage = getErrorMessage(errorId) || "Ошибка при отправке кода"
            setSendEmailCodeError(errorMessage)

            if (errorId === 'email_confirmation_not_found' ||
                errorId === 'email_confirmation_invalid' ||
                errorId === 'email_not_confirmed') {
                setEmailConfirmationId(null)
                setEmailCodeSent(false)
            }
        } finally {
            setIsSendingEmailCode(false)
        }
    }

    const onSubmit: SubmitHandler<RegistrationForm> = async (data) => {
        if (!phoneConfirmationId) {
            setSendCodeError("Необходимо отправить код подтверждения телефона")
            return
        }

        if (!emailConfirmationId) {
            setSendEmailCodeError("Необходимо отправить код подтверждения email")
            return
        }

        const requestData: IRegisterOrganizationRequest = {
            inn: data.inn,
            organization_name: data.organization_name,
            email: data.email,
            phone: data.phone,
            password: data.password,
            phone_confirmation_id: phoneConfirmationId,
            phone_confirmation_code: data.phone_confirmation_code,
            email_confirmation_id: emailConfirmationId,
            email_confirmation_code: data.email_confirmation_code,
            ...(data.kpp && { kpp: data.kpp }),
            ...(data.legal_full_name && { legal_full_name: data.legal_full_name }),
            ...(data.registration_address && { registration_address: data.registration_address }),
        }

        const result = await dispatch(fetchRegisterOrganization(requestData))
        if (fetchRegisterOrganization.fulfilled.match(result)) {
            navigate('/')
        } else if (fetchRegisterOrganization.rejected.match(result)) {
            const errorId = (result.payload as any)?.error_id
            if (errorId) {
                const errorMessage = getErrorMessage(errorId)

                // Handle email confirmation errors
                if (errorId === 'email_confirmation_not_found' ||
                    errorId === 'email_confirmation_mismatch' ||
                    errorId === 'email_confirmation_invalid' ||
                    errorId === 'email_not_confirmed') {
                    setError('email_confirmation_code', {
                        type: 'server',
                        message: errorMessage
                    })
                    setEmailConfirmationId(null)
                    setEmailCodeSent(false)
                }

                // Handle phone confirmation errors
                if (errorId === 'phone_confirmation_invalid' ||
                    errorId === 'phone_confirmation_mismatch' ||
                    errorId === 'phone_not_confirmed') {
                    setError('phone_confirmation_code', {
                        type: 'server',
                        message: errorMessage
                    })
                    setPhoneConfirmationId(null)
                    setCodeSent(false)
                }
            }
        }
    }

    return (
        <div className="grid grid-cols-12 flex-1 w-full min-w-0 overflow-auto">
            <div className="col-span-6 relative bg-color-dark hidden xl:flex items-center justify-center">
                <img src={AuthPreview} className="object-cover absolute w-full h-full" />
                <LogoMonoSvg className="relative z-10" width={530} height={145} />
            </div>
            <div className="col-span-12 xl:col-span-6 flex items-center justify-center">
                <div className="box min-w-[600px]">
                    <div className="box_header font-mediumttf !mb-4">
                        Регистрация организации
                    </div>
                    {authError &&
                        <div className="bg-red-100 mb-3 p-4 flex items-center text-red-500 border-b-2 rounded-lg border-red-500">
                            <WarningSvg width={32} height={32} className="mt-1 flex-shrink-0" />
                            <div className="text-color-dark ml-2 whitespace-pre-line">
                                {getErrorMessage(authError?.error_id)}
                            </div>
                        </div>
                    }
                    <form onSubmit={handleSubmit(onSubmit)} className="flex overflow-auto flex-col gap-6">
                        <div className="flex max-h-[400px] overflow-auto flex-col gap-6">
                            <div>
                                <FormInput
                                    error={errors['inn']}
                                    placeholder="Введите ИНН"
                                    register={register}
                                    label="ИНН"
                                    id={"inn"}
                                    required={true}
                                />
                                {isLoadingDadata && (
                                    <div className="mt-2 text-sm text-color-primary">
                                        Поиск организации...
                                    </div>
                                )}
                                {isAutoFilled && !isLoadingDadata && (
                                    <div className="mt-2 text-sm text-green-600">
                                        Данные организации найдены и заполнены автоматически
                                    </div>
                                )}
                                {dadataError && !isLoadingDadata && (
                                    <div className="mt-2 text-sm text-amber-600 flex items-center">
                                        <WarningSvg width={16} height={16} className="mr-1" />
                                        {dadataError}. Вы можете заполнить поля вручную.
                                    </div>
                                )}
                            </div>
                            <FormInput
                                error={errors['kpp']}
                                placeholder="Введите КПП"
                                register={register}
                                label="КПП"
                                id={"kpp"}
                                required={false}
                            />
                            <FormInput
                                error={errors['organization_name']}
                                placeholder="Введите наименование организации"
                                register={register}
                                label="Наименование организации"
                                id={"organization_name"}
                                required={true}
                            />
                            <FormInput
                                error={errors['legal_full_name']}
                                placeholder="Введите полное юридическое наименование"
                                register={register}
                                label="Полное юридическое наименование"
                                id={"legal_full_name"}
                                required={false}
                            />
                            <FormInput
                                error={errors['registration_address']}
                                placeholder="Введите юридический адрес"
                                register={register}
                                label="Юридический адрес"
                                id={"registration_address"}
                                required={false}
                            />
                            <div>
                                <FormInput
                                    error={errors['email']}
                                    placeholder="Введите email"
                                    register={register}
                                    label="Email"
                                    id={"email"}
                                    type="email"
                                    required={true}
                                />
                                <button
                                    type="button"
                                    onClick={handleSendEmailConfirmationCode}
                                    disabled={isSendingEmailCode || emailResendTimer > 0 || !emailValue || emailValue.trim() === ""}
                                    className="mt-2 text-sm text-color-primary disabled:text-gray-400 disabled:cursor-not-allowed hover:underline"
                                >
                                    {isSendingEmailCode
                                        ? "Отправка..."
                                        : emailResendTimer > 0
                                            ? `Повторная отправка через ${emailResendTimer}с`
                                            : emailCodeSent
                                                ? "Отправить код повторно"
                                                : "Отправить код подтверждения"}
                                </button>
                                {sendEmailCodeError && (
                                    <div className="mt-2 text-sm text-red-500 flex items-center">
                                        <WarningSvg width={16} height={16} className="mr-1" />
                                        {sendEmailCodeError}
                                    </div>
                                )}
                                {emailCodeSent && !sendEmailCodeError && (
                                    <div className="mt-2 text-sm text-green-600">
                                        Код подтверждения отправлен на {emailValue}
                                    </div>
                                )}
                            </div>
                            <FormInput
                                error={errors['email_confirmation_code']}
                                placeholder="Введите код подтверждения email"
                                register={register}
                                label="Код подтверждения email"
                                id={"email_confirmation_code"}
                                required={true}
                            />
                            <div>
                                <Controller
                                    name="phone"
                                    control={control}
                                    render={({ field }) => (
                                        <PhoneInput
                                            id="phone"
                                            label="Телефон"
                                            value={field.value}
                                            onChange={field.onChange}
                                            error={errors.phone}
                                            required={true}
                                        />
                                    )}
                                />
                                <button
                                    type="button"
                                    onClick={handleSendConfirmationCode}
                                    disabled={isSendingCode || resendTimer > 0 || !phoneValue || phoneValue.trim() === ""}
                                    className="mt-2 text-sm text-color-primary disabled:text-gray-400 disabled:cursor-not-allowed hover:underline"
                                >
                                    {isSendingCode
                                        ? "Отправка..."
                                        : resendTimer > 0
                                            ? `Повторная отправка через ${resendTimer}с`
                                            : codeSent
                                                ? "Отправить код повторно"
                                                : "Отправить код подтверждения"}
                                </button>
                                {sendCodeError && (
                                    <div className="mt-2 text-sm text-red-500 flex items-center">
                                        <WarningSvg width={16} height={16} className="mr-1" />
                                        {sendCodeError}
                                    </div>
                                )}
                                {codeSent && !sendCodeError && (
                                    <div className="mt-2 text-sm text-green-600">
                                        Код подтверждения отправлен на номер {phoneValue}
                                    </div>
                                )}
                            </div>
                            <FormInput
                                error={errors['phone_confirmation_code']}
                                placeholder="Введите код подтверждения"
                                register={register}
                                label="Код подтверждения телефона"
                                id={"phone_confirmation_code"}
                                required={true}
                            />
                            <FormInput
                                error={errors['password']}
                                placeholder="******"
                                register={register}
                                label="Пароль"
                                type="password"
                                id={"password"}
                                required={true}
                            />
                            <FormInput
                                error={errors['confirm_password']}
                                placeholder="******"
                                register={register}
                                label="Подтверждение пароля"
                                type="password"
                                id={"confirm_password"}
                                required={true}
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="text-[22px] text-white justify-center inline-flex gap-2 w-full text-center bg-color-dark rounded-full py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isLoading ? 'Регистрация...' : 'Зарегистрироваться'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    )
}
